<template>
  <nav class="gb-navbar navbar navbar-default navbar-static-top uiv beta-nav">
    <div class="container">
      <div class="navbar-header">
        <!-- Mobile Navbar -->
        <button
          type="button"
          role="button"
          aria-expanded="false"
          aria-haspopup="menu"
          class="navbar-toggle collapsed"
          @click="showNavbar = !showNavbar">
          <span class="sr-only">Toggle navigation</span>
          <i class="fa fa-lg fa-bars"/>
        </button>
        <router-link
          class="hidden-lg hidden-xl"
          :to="{name: 'Home', query: {ref: 'navbar'}}"
          aria-label="Home">
          <img
            class="mobile-logo"
            src="~@/assets/app_switcher/toolkit_circle.png"
            alt="">
        </router-link>
      </div>

      <!-- Left Navigation Items -->
      <collapse class="navbar-collapse collapse" v-model="showNavbar">
        <ul class="nav navbar-nav">
          <li>
            <router-link
              v-if="isAuthenticated"
              :to="{name: 'Home', query: {ref: 'navbar'}}"
              class="home-link">
              <!-- Logo -->
              <img
                class="app-logo visible-lg"
                src="~@/assets/app_switcher/toolkit_circle.png"
                alt="">
              <span class="hidden-md">Goalbook Toolkit</span>
            </router-link>
          </li>
          <!-- Instructional Design Tools Dropdown-->
          <A11yDropdown
            :dropdown-props="{
              tag:'li',
              ref:'instructionalResourcesDropdown',
            }"
            :button-props="{
              class:'dropdown-toggle',
              'data-role':'trigger',
              'aria-labelledby':'instructional-design-tools-label'
            }"
            :dom-attributes="{
              role:'menu',
              id:'instructional-design-tools-dropdown'
            }"
          >
            <template slot="button-contents"><span id="instructional-design-tools-label" aria-hidden="true">Instructional <span :class="editorVisibility ? 'design-text' : ''">Design</span> Tools <span class="caret"/></span></template>
            <template slot="dropdown-items">
              <li role="presentation">
                <a
                  role="menuitem"
                  v-if="(iodVisibility || effectsVisibility) && aiFeatureVisibility"
                  :href="presentlevelsBetaURL">Present Levels Wizard</a>
                <router-link
                  role="menuitem"
                  v-else
                  :to="{name: 'PresentLevelsNewWizard', query: {ref: 'navbar'}}">Present Levels Wizard</router-link>
              </li>
              <li v-if="(!iodVisibility || !aiFeatureVisibility) && !effectsVisibility" role="presentation"><router-link role="menuitem" :to="{name: 'BrowsePresentLevelsPage', query: {ref: 'navbar'}}">Browse Present Levels</router-link></li>
              <li v-if="iodVisibility" role="presentation">
                <a role="menuitem" :href="impactOfDisabilityURL">Compose Impact of Disability</a>
              </li>
              <li class="divider" role="separator"/>
              <li v-if="aiFeatureVisibility" role="presentation" >
                <router-link role="menuitem" :to="{name: 'AssessmentWizard', query: {ref: 'navbar'}}">
                  Assessment Wizard
                </router-link>
              </li>
              <li role="presentation"><router-link role="menuitem" :to="{name: 'BrowseAssessments', query: {ref: 'navbar'}}">Browse Assessments</router-link></li>
              <li class="divider" role="separator"/>
              <li role="presentation"><router-link role="menuitem" :to="{name: 'BrowseGoalsWizardModal', query: {ref: 'navbar'}}">Learning Goal Wizard</router-link></li>
              <li role="presentation"><router-link role="menuitem" :to="{name: 'BrowseGoalsPage', query: {ref: 'navbar'}}">Browse Learning Goals</router-link></li>
              <li role="presentation"><router-link role="menuitem" :to="{name: 'GoalIndexPage', query: {ref: 'navbar'}}">Learning Goal Index</router-link></li>
              <li class="divider" role="separator"/>
              <li role="presentation"><router-link role="menuitem" :to="{name: 'StrategyWizard', query: {ref: 'navbar'}}">Strategy Wizard</router-link></li>
              <li role="presentation"><router-link role="menuitem" :to="{name: 'BrowseStrategiesPage', query: {ref: 'navbar'}}">Browse Strategies</router-link></li>
              <li role="presentation"><router-link role="menuitem" :to="{name: 'StrategyIndex', query: {ref: 'navbar'}}">UDL Strategy Index</router-link></li>
            </template>
          </A11yDropdown>

          <!-- My Library Dropdown-->
          <A11yDropdown
            :dropdown-props="{
              tag:'li',
              ref:'yourContentDropdown',
            }"
            :button-props="{
              class:'dropdown-toggle',
              'data-role':'trigger',
              'aria-labelledby':'my-library-label'
            }"
            :dom-attributes="{
              role:'menu',
              id:'my-library-dropdown'
            }"
          >
            <template slot="button-contents"><span id="my-library-label" aria-hidden="true">My Library <span class="caret"/></span></template>
            <template slot="dropdown-items">
              <li role="presentation">
                <a role="menuitem" :href="`${this.home}/v/library/all${betaVisibility ? '?beta=true' : ''}`">All Folders</a>
              </li>
              <li class="divider" role="separator"/>
              <li role="presentation">
                <a role="menuitem" :href="`${this.home}/v/library/owned`">My Folders</a>
              </li>
              <li role="presentation">
                <a role="menuitem" :href="`${this.home}/v/library/shared`">Shared With Me</a>
              </li>
              <li class="divider" role="separator"/>
              <li role="presentation">
                <a role="menuitem" :href="`${this.home}/v/library/recently_saved`">Recently Saved Content</a>
              </li>
              <li role="presentation">
                <a role="menuitem" :href="`${this.home}/v/library/recently_viewed/goals`">Recently Viewed Content</a>
              </li>
            </template>
          </A11yDropdown>

          <!-- Training Center Dropdown -->
          <A11yDropdown
            v-if="trainingCenterVisibility"
            :dropdown-props="{
              tag:'li',
              ref:'trainingCenterDropdown',
            }"
            :button-props="{
              class:'dropdown-toggle',
              'data-role':'trigger',
              'aria-labelledby':'training-center-label'
            }"
            :dom-attributes="{
              role:'menu',
              id:'training-center-dropdown'
            }"
          >
            <template slot="button-contents"><span id="training-center-label" aria-hidden="true">Training Center <span class="caret"/></span></template>
            <template slot="dropdown-items">
              <li v-if="aiFeatureVisibility || nycWebinarsVisibility" role="presentation"><a
                role="menuitem"
                tabindex="0"
                :href="nycWebinarsVisibility ? nycWebinars : webinars"
                target="_blank"
                rel="noopener"
                @click="trackLinkClick('Clicked Upcoming Webinars', {ref: 'navbar'})">Upcoming Webinars
                <OpensInNewTabIcon :external-link="true" />
              </a>
              </li>
              <li
                v-if="showTraining && trainingCenterVisibility"
                role="presentation"><a
                tabindex="0"
                role="menuitem"
                @click="sendToLMS()">On-Demand Courses
                <OpensInNewTabIcon :external-link="true" />
              </a>
              </li>
              <li v-if="aiFeatureVisibility" role="presentation"><a
                role="menuitem"
                tabindex="0"
                :href="toolkitUpdates"
                target="_blank"
                rel="noopener"
                @click="trackLinkClick('Clicked View All Posts', {ref: 'navbar'})"
              >Recent Updates
                <OpensInNewTabIcon :external-link="true" />
              </a>
              </li>
            </template>
          </A11yDropdown>
          <!-- Admin dropdown -->
          <A11yDropdown
            v-if="editorVisibility"
            :dropdown-props="{
              ref:'adminDropdown',
              tag:'li'
            }"
            :button-props="{
              'data-role':'trigger',
              'aria-labelledby':'admin-label'
            }"
            :dom-attributes="{
              role:'menu',
              id:'admin-dropdown'
            }"
          >
            <template slot="button-contents"><span id="admin-label" aria-hidden="true">Admin <span class="caret"/></span></template>
            <template slot="dropdown-items">
              <li role="presentation"><a role="menuitem" :href="editorLinks.strategiesAdmin">UDL Strategies Admin</a></li>
              <li role="presentation"><a role="menuitem" :href="editorLinks.goalsAdmin">Goals Admin</a></li>
              <li role="presentation"><a role="menuitem" :href="editorLinks.researchCitations">Research Citations</a></li>
              <li role="presentation"><a role="menuitem" :href="editorLinks.myGoals(user.uuid)">My Goals</a></li>
              <li class="divider" role="separator"/>
              <li role="presentation"><a role="menuitem" :href="editorLinks.allStandards">Admin Standards</a></li>
              <li role="presentation"><a role="menuitem" :href="editorLinks.udlStandards">UDL Standards</a></li>
              <li class="divider" role="separator"/>
              <li role="presentation"><router-link role="menuitem" :to="{name: 'NewAnchorAccessPoints'}">New Anchor with Access Points</router-link></li>
              <li role="presentation"><a role="menuitem" :href="editorLinks.addGoal">Add Goal</a></li>
              <li role="presentation"><a role="menuitem" :href="editorLinks.addStrategy">Add Strategy</a></li>
              <li role="presentation"><a role="menuitem" :href="editorLinks.addResearchCitation">Add Research Citation</a></li>
            </template>
          </A11yDropdown>
        </ul>
        <!-- Right Navigation Items -->
        <p class="navbar-text navbar-right text-center" v-if="fetching"><i class="fa fa-circle-o-notch fa-spin"/></p>
        <user-menu v-if="!fetching && isAuthenticated" class="navbar-right" />
      </collapse>
    </div>
    <on-demand-training-error-modal :show-error-modal="showErrorModal" :on-close="closeModal"/>
  </nav>
</template>

  <script>
  import { mapGetters, mapState } from 'vuex'
  import UserMenu from './UserMenu'
  import { toolkitUrls } from '@/links'
  import A11yDropdown from '@/components/A11yDropdown'
  import { sendToLMS } from '@/api/auth'
  import OnDemandTrainingErrorModal from '@/components/OnDemandTrainingErrorModal'
  import homeMixin from '@/components/home/mixins'
  import OpensInNewTabIcon from '../OpensInNewTabIcon'
  import BetaBadge from '@/components/BetaBadge'

  export default {
    name: 'GbNavbar',
    mixins: [homeMixin],
    components: {
      A11yDropdown,
      UserMenu,
      OnDemandTrainingErrorModal,
      OpensInNewTabIcon,
      BetaBadge
    },
    data() {
      return {
        ...toolkitUrls,
        showNavbar: false,
        showErrorModal: false,
      }
    },
    computed: {
      ...mapGetters('user', [
        'isAuthenticated',
        'isTrial',
        'isPremium',
        'isEnterprise',
        'daysLeftInLicense'
      ]),
      ...mapState('user', ['fetching', 'user']),
      ...mapState('user', {
        showTraining: state => !!state.user.show_training_button
      }),
      ...mapGetters('visibility', ['editorVisibility', 'betaVisibility', 'iodVisibility', 'effectsVisibility', 'aiFeatureVisibility', 'nycWebinarsVisibility']),
      trainingCenterVisibility() {
        return this.aiFeatureVisibility || this.nycWebinarsVisibility
      },
      presentlevelsBetaURL() {
        const url = new URL(this.presentlevels.v2)
        url.searchParams.set('ref', 'navbar')
        if (this.betaVisibility) {
          url.searchParams.set('beta', 'true')
        }
        return url
      },
      impactOfDisabilityURL() {
        const url = new URL(this.impactOfDisability(this.aiFeatureVisibility, this.betaVisibility))
        url.searchParams.set('ref', 'navbar')
        return url
      }
    },
    methods: {
      async sendToLMS() {
        try {
          this.$sendEvent('Clicked On Demand Training Button', {ref: 'navbar'})
          await sendToLMS()
          this.$sendEvent('Logged Into On Demand Training')
        } catch (err) {
          this.$sendEvent('On Demand Training Login Failed')
          this.showErrorModal = true
        }
      },
    closeModal() {
        this.showErrorModal = false
    },
    }
  }
  </script>

  <style lang="less">
  @import '~@/variables.less';

  .gb-navbar.navbar.navbar-default {
    height: 50px; // cap this so the license banner can overflow out of it
    @media (max-width: @screen-xs-max) {
      height: initial;
    }
    border-bottom: 0;
    box-shadow: none;
    background: @gb_blue_medium;
    .navbar-text {
      color: #bee3ff;
    }

    .navbar-nav .open .dropdown-menu {
      background: white;
    }
    .active-nav-link {
      background: @gb_blue_dark;
      color: white;
    }
    .navbar-nav > li > a {
      font-size: 18px;
      color: #fff;
    }
    .navbar-nav > li.open > a {
      .active-nav-link;
    }
    .navbar-nav > li > a,
    .navbar-nav > li.active > a,
    .navbar-nav > li.open > a,
    a.navbar-brand {
      &:hover,
      &:focus-visible {
        .active-nav-link;
      }
    }
    .navbar-nav > li.active > a,
    .navbar-nav > li.open > a {
      .active-nav-link;
    }

    .navbar-toggle {
      border-color: transparent;
      &:hover,
      &:active,
      &:focus {
        background-color: @gb_blue_dark;
      }
      .icon-bar {
        background-color: white;
      }
    }
  }

  // to make sure the text stays dark on smaller screens
  @media (max-width: 767px) {
    .navbar-default .navbar-nav .open .dropdown-menu > li > a {
      color: #333 !important;
    }
  }
  </style>

  <style scoped lang="less">
    @import '~@/variables.less';
    img.app-logo {
      height: 32px;
      width: auto;
    }
    img.mobile-logo {
      height: 32px;
      width: auto;
      margin: 10px;
    }
    .home-link {
      height: 50px;
      width: 200px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .fa.fa-bars {
      color: #fff;
    }
    .dropdown-menu > li > a{
      cursor: pointer;
    }

    .new-pl-link {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    @media (max-width: 1200px) {
      .home-link {
        display: none;
      }
    }
    @media (min-width: 768px) and (max-width: 1000px) {
      .container {
        width: 100%;
      }
      .navbar-right {
        float: none !important;
      }
    }

    // For editor users that see the "Admin" dropdown, this is necessary to make everything in the navbar fit
    @media (min-width: 768px) and (max-width: 802px) {
      .design-text {
        display: none;
      }
    }
  </style>
